import axios from 'axios';

const baseURL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  withCredentials: false,
  timeout: 12000
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const message = err.response?.data?.message || err.message || 'Request failed';
    const errors = err.response?.data?.errors || null;
    return Promise.reject({ message, errors, status: err.response?.status });
  }
);

export const sessionId = (() => {
  if (typeof window === 'undefined') return 'guest';
  let id = window.localStorage.getItem('meed.session');
  if (!id) {
    id = (crypto?.randomUUID?.() || `s${Date.now()}${Math.random().toString(36).slice(2, 8)}`).replace(/[^A-Za-z0-9\-_.]/g, '');
    window.localStorage.setItem('meed.session', id);
  }
  return id;
})();

export const catalog = {
  home: () => api.get('/home').then((r) => r.data.data),
  categories: () => api.get('/catalog/categories').then((r) => r.data.data),
  products: (params) => api.get('/catalog/products', { params }).then((r) => r.data.data),
  product: (id) => api.get(`/catalog/products/${id}`).then((r) => r.data.data)
};

export const cart = {
  show: () => api.get('/cart', { params: { session_id: sessionId } }).then((r) => r.data.data),
  add: (productId, qty = 1) => api.post('/cart/items', { session_id: sessionId, product_id: productId, qty }).then((r) => r.data.data),
  setQty: (productId, qty) => api.patch('/cart/items', { session_id: sessionId, product_id: productId, qty }).then((r) => r.data.data)
};

export const orders = {
  checkout: (payload) => api.post('/checkout/order', { session_id: sessionId, ...payload }).then((r) => r.data.data),
  show: (id) => api.get(`/orders/${id}`).then((r) => r.data.data),
  track: (orderNumber, phone) => api.get(`/track/${orderNumber}`, { params: { phone } }).then((r) => r.data.data),
  rate: (id, payload) => api.post(`/orders/${id}/rating`, payload).then((r) => r.data.data)
};

export const auth = {
  sendOtp: (phone) => api.post('/auth/otp/send', { phone }).then((r) => r.data.data),
  verifyOtp: (phone, code, name, referralCode) =>
    api.post('/auth/otp/verify', { phone, code, name, referral_code: referralCode }).then((r) => r.data.data),
  me: () => api.get('/auth/me').then((r) => r.data.data),
  logout: () => api.post('/auth/logout').then((r) => r.data.data)
};

export const addresses = {
  list: () => api.get('/me/addresses').then((r) => r.data.data),
  create: (payload) => api.post('/me/addresses', payload).then((r) => r.data.data),
  update: (id, payload) => api.patch(`/me/addresses/${id}`, payload).then((r) => r.data.data),
  remove: (id) => api.delete(`/me/addresses/${id}`).then((r) => r.data.data)
};

export const coupons = {
  preview: (code) => api.post('/coupons/preview', { code, session_id: sessionId }).then((r) => r.data.data)
};

export const wallet = {
  show: () => api.get('/me/wallet').then((r) => r.data.data)
};

export const referral = {
  show: () => api.get('/me/referral').then((r) => r.data.data)
};

export const slots = {
  list: (date, zone) => api.get('/delivery/slots', { params: { date, zone } }).then((r) => r.data.data)
};

export function setAuthToken(token) {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    if (typeof window !== 'undefined') window.localStorage.setItem('meed.token', token);
  } else {
    delete api.defaults.headers.common.Authorization;
    if (typeof window !== 'undefined') window.localStorage.removeItem('meed.token');
  }
}

// Restore auth token if present
if (typeof window !== 'undefined') {
  const saved = window.localStorage.getItem('meed.token');
  if (saved) api.defaults.headers.common.Authorization = `Bearer ${saved}`;
}
